import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, RevisionIdSchema, RunIdSchema, TraceIdSchema } from "../primitives/ids.js";
import { RunStatusSchema } from "./generation-run.js";
import { StudioProjectModeV1Schema } from "./external-site-proof.js";
import { CheckSeveritySchema } from "./quality-gate-result.js";
import { SitePackageIdSchema } from "./site-package.js";
export const StudioQaRollupSchema = z.enum(["PASS", "FAIL", "PENDING", "UNKNOWN"]);
const ROLLUP_PENDING_RUN_STATUSES = [
    "PENDING",
    "GENERATING",
    "GENERATED",
    "QA_QUEUED",
    "QA_RUNNING",
];
const ROLLUP_PASS_RUN_STATUSES = [
    "QA_PASSED",
    "QA_PASSED_WITH_WARNINGS",
];
const ROLLUP_FAIL_RUN_STATUSES = ["QA_FAILED", "FAILED", "CANCELLED"];
/**
 * Single source of truth for the Studio QA rollup badge (Lane G).
 * Derives strictly from persisted run data: the quality-gate envelope status when present,
 * otherwise the run lifecycle status. Never from in-memory/derived API state.
 */
export function resolveStudioQaRollup(qaGateStatus, runStatus) {
    if (qaGateStatus) {
        if (qaGateStatus === "QA_PASS" || qaGateStatus === "QA_WARN") {
            return "PASS";
        }
        if (qaGateStatus === "QA_FAIL") {
            return "FAIL";
        }
        return "PENDING";
    }
    if (ROLLUP_PENDING_RUN_STATUSES.includes(runStatus)) {
        return "PENDING";
    }
    if (ROLLUP_PASS_RUN_STATUSES.includes(runStatus)) {
        return "PASS";
    }
    if (ROLLUP_FAIL_RUN_STATUSES.includes(runStatus)) {
        return "FAIL";
    }
    return "UNKNOWN";
}
export const StudioHashPrefixSchema = z.union([
    z.literal(""),
    z.string().regex(/^[0-9a-f]{12}$/, "artifactSetHashPrefix must be exactly 12 lowercase hex chars or empty"),
]);
export const StudioProjectGenerationLockV1Schema = z
    .object({
    locked: z.boolean(),
    runId: RunIdSchema.nullable(),
    lockedAt: z.string().datetime().nullable(),
})
    .strict();
export const StudioProjectSummaryV1Schema = z
    .object({
    schemaId: z.literal("studio.projectSummary.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    projectMode: StudioProjectModeV1Schema,
    lastRunUpdatedAt: z.string().datetime(),
    lastKnownRunStatus: RunStatusSchema,
    currentBusinessGraphRevisionId: RevisionIdSchema.nullable(),
    generationLock: StudioProjectGenerationLockV1Schema.optional(),
})
    .strict();
export const StudioRunRowV1Schema = z
    .object({
    schemaId: z.literal("studio.runRow.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    status: RunStatusSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    recipeId: z.string().min(1),
    qaRollup: StudioQaRollupSchema,
    artifactSetHashPrefix: StudioHashPrefixSchema,
})
    .strict();
/** Persisted package downgrade explanation for Studio recovery (Phase 3). */
export const StudioPackageResolutionV1Schema = z
    .object({
    agencyRequestedSitePackageId: SitePackageIdSchema,
    builtSitePackageId: SitePackageIdSchema,
    downgradeApplied: z.boolean(),
    reasons: z.array(z.string().min(1)),
    richnessScore: z.number().int().min(0).max(100).optional(),
    minimumRichnessRequired: z.number().int().min(0).max(100).optional(),
    evaluatedAt: z.string().datetime(),
})
    .strict();
/**
 * True only when a recorded downgrade matches the persisted project package.
 * Suppresses invented agency requests (e.g. Standard 5) when the project was
 * always the built package (e.g. Basic 3). Missing project package fails closed.
 */
export function isTrustworthyPackageResolution(resolution, persistedProjectSitePackageId, builtPackageFromRecipe) {
    if (!resolution.downgradeApplied) {
        return false;
    }
    if (resolution.agencyRequestedSitePackageId === resolution.builtSitePackageId) {
        return false;
    }
    const projectPackage = SitePackageIdSchema.safeParse(persistedProjectSitePackageId);
    if (!projectPackage.success) {
        return false;
    }
    if (resolution.agencyRequestedSitePackageId !== projectPackage.data) {
        return false;
    }
    if (resolution.builtSitePackageId === projectPackage.data) {
        return false;
    }
    if (typeof builtPackageFromRecipe === "string" &&
        builtPackageFromRecipe.length > 0 &&
        resolution.builtSitePackageId !== builtPackageFromRecipe) {
        return false;
    }
    return true;
}
export const StudioRunDetailV1Schema = StudioRunRowV1Schema.extend({
    schemaId: z.literal("studio.runDetail.v1"),
    packPinSummary: z
        .object({
        recipeId: z.string().min(1),
    })
        .strict(),
    traceId: TraceIdSchema.optional(),
    safeFailure: z
        .object({
        code: z.string().min(1),
        message: z.string().min(1),
    })
        .strict()
        .nullable(),
    /** Human-readable QA check reasons (failed FAIL/WARN checks) for Studio display. */
    qaReasons: z.array(z.string().min(1)).optional(),
    /** Structured QA issues with checkId and severity for Studio resolve flows. */
    qaIssues: z
        .array(z
        .object({
        checkId: z.string().min(1),
        severity: CheckSeveritySchema,
        message: z.string().min(1),
    })
        .strict())
        .optional(),
    /** Persisted requested-vs-built package resolution when a downgrade was accepted. */
    packageResolution: StudioPackageResolutionV1Schema.optional(),
}).strict();
/** Phase 1 — agency-wide Mission Control counters for Studio command room. */
export const StudioMissionControlSummaryV1Schema = z
    .object({
    schemaId: z.literal("studio.missionControlSummary.v1"),
    agencyId: AgencyIdSchema,
    generatedAt: z.string().datetime(),
    blockedProjectCount: z.number().int().min(0),
    qaFailureCount: z.number().int().min(0),
    clientsAwaitingApprovalCount: z.number().int().min(0),
    handoffReadyCount: z.number().int().min(0),
})
    .strict();
export const StudioCreateRunResultV1Schema = z
    .object({
    runId: RunIdSchema,
    idempotentReplay: z.boolean().optional(),
})
    .strict();
/**
 * Phase 3 — agency-side rendered website preview for the latest passing artifact.
 * Lets Studio embed the generated site without leaving the app. Read-only; the
 * HTML is served from the same render artifacts the Client Portal preview uses.
 */
export const StudioProjectPreviewV1Schema = z
    .object({
    schemaId: z.literal("studio.projectPreview.v1"),
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    /** Route path currently rendered (e.g. "/"). */
    previewRoute: z.string().min(1),
    /** All route paths available in the rendered site package. */
    availableRoutes: z.array(z.string().min(1)).min(1),
    /** Fully-rendered HTML document for the selected route. */
    previewHtml: z.string().min(1),
    artifactSetHashPrefix: StudioHashPrefixSchema,
    updatedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=studio-dashboard.js.map